import React from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import { CalendarPicker, PickersDay } from "@mui/x-date-pickers";
import { CircularProgress } from "@mui/material";
import AlertDialog from "components/AlertDialog";
import DeliveryTimeSlotsQuery from "./DeliveryTimeSlotsQuery.gql";
import UpdateClientOrderTimeSlotMutation from "./UpdateClientOrderTimeSlotMutation.gql";
import NoSuitableTimeSlotMutation from "./NoSuitableTimeSlotMutation.gql";
import { DeliveryTimeSlot, Order } from "@types";
import TimeSlotSelector from "./TimeSlotSelector";
import ClientPortalConfigQuery from "pages/Delivery/ClientPortalConfigQuery.gql";

interface AppointmentProps {
  order: Order;
  onSubmitSuccess?: () => void;
}

const getTimeSlotKey = (timeSlot: DeliveryTimeSlot | null) => {
  if (!timeSlot) return null;
  // Include hour_from, office_id and delivery_type to ensure uniqueness
  const key = `${timeSlot.date}_${timeSlot.halfday}_${timeSlot.hour_from}`;
  const withOffice = timeSlot.office_id ? `${key}_${timeSlot.office_id}` : key;
  return timeSlot.delivery_type
    ? `${withOffice}_${timeSlot.delivery_type}`
    : withOffice;
};

/**
 * Build a map of date -> Set of special slot types ("afterwork" | "next_day")
 * Used to show indicators on calendar days that have special timeslots.
 */
const buildSpecialSlotsMap = (
  deliveryTimeSlots: DeliveryTimeSlot[] | undefined,
): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();
  if (!deliveryTimeSlots) return map;
  for (const slot of deliveryTimeSlots) {
    if (slot.is_after_work) {
      if (!map.has(slot.date)) map.set(slot.date, new Set());
      map.get(slot.date)!.add("afterwork");
    }
    if (slot.is_next_day) {
      if (!map.has(slot.date)) map.set(slot.date, new Set());
      map.get(slot.date)!.add("next_day");
    }
  }
  return map;
};

/** Merge new time slots into existing, deduplicating by key and sorting by date */
const mergeTimeSlots = (
  existing: DeliveryTimeSlot[],
  incoming: DeliveryTimeSlot[],
): DeliveryTimeSlot[] => {
  if (existing.length === 0) return incoming;
  const existingKeys = new Set(existing.map(getTimeSlotKey));
  const newSlots = incoming.filter(
    (slot) => !existingKeys.has(getTimeSlotKey(slot)),
  );
  if (newSlots.length === 0) return existing;
  return [...existing, ...newSlots].sort((a, b) =>
    a.date.localeCompare(b.date),
  );
};

const disableAllYears = () => true;

const Appointment = ({ order, onSubmitSuccess }: AppointmentProps) => {
  const confirmedDate = React.useMemo<dayjs.Dayjs | null>(() => {
    return order?.timeSlot?.date ? dayjs(order?.timeSlot?.date) : null;
  }, [order?.timeSlot?.date]);
  const [openDialog, setOpenDialog] = React.useState<boolean>(false);
  const minDate = React.useMemo<dayjs.Dayjs>(
    () => dayjs(Date.now()).add(1, "day"),
    [],
  );
  const [selectedDeliveryDate, setSelectedDeliveryDate] =
    React.useState<Dayjs | null>(confirmedDate ?? null);
  const [selectedTimeSlot, setSelectedTimeSlot] = React.useState<string | null>(
    getTimeSlotKey(order?.timeSlot),
  );
  const [noSuitableTimeSlot, setNoSuitableTimeSlot] =
    React.useState<boolean>(false);

  // Accumulated time slots from all fetches (incremental loading)
  const [deliveryTimeSlots, setDeliveryTimeSlots] = React.useState<
    DeliveryTimeSlot[]
  >([]);
  const [initialLoadComplete, setInitialLoadComplete] = React.useState(false);
  const fetchedUpToRef = React.useRef<string | null>(null);

  const { loading: loadingClientPortalConfig, data: clientPortalConfigData } =
    useQuery(ClientPortalConfigQuery);

  const maxDate = React.useMemo<dayjs.Dayjs | undefined>(() => {
    const maxDateString =
      clientPortalConfigData?.clientPortalConfig?.contractorConfig
        ?.maxDeliveryDate;
    return maxDateString ? dayjs(maxDateString) : undefined;
  }, [
    clientPortalConfigData?.clientPortalConfig?.contractorConfig
      ?.maxDeliveryDate,
  ]);

  const isNoSuitableTimeslotAvailable = React.useMemo<boolean>(() => {
    return (
      clientPortalConfigData?.clientPortalConfig?.contractorConfig
        ?.is_no_suitable_timeslot_message_enabled ?? true
    );
  }, [
    clientPortalConfigData?.clientPortalConfig?.contractorConfig
      ?.is_no_suitable_timeslot_message_enabled,
  ]);

  const getEndDateForFetch = React.useCallback(
    (monthDate: dayjs.Dayjs) => {
      // if maxDate exists and current month in the calendar is after or the same as maxDate, limit the end date to maxDate
      if (
        maxDate &&
        (monthDate.isAfter(maxDate, "month") ||
          monthDate.isSame(maxDate, "month"))
      ) {
        return maxDate.format("YYYY-MM-DD");
      }
      return monthDate.endOf("month").format("YYYY-MM-DD");
    },
    [maxDate],
  );

  const [fetchTimeSlots] = useLazyQuery(DeliveryTimeSlotsQuery, {
    fetchPolicy: "network-only",
    onCompleted: (data: any) => {
      if (!data?.deliveryTimeSlots) return;
      setDeliveryTimeSlots((prev) =>
        mergeTimeSlots(prev, data.deliveryTimeSlots),
      );
      setInitialLoadComplete(true);
    },
  });

  const specialSlotsMap = React.useMemo(
    () => buildSpecialSlotsMap(deliveryTimeSlots),
    [deliveryTimeSlots],
  );

  // O(1) date availability lookup (replaces O(n) hasTimeSlot per day cell)
  const availableDateSet = React.useMemo(() => {
    const set = new Set<string>();
    if (deliveryTimeSlots) {
      for (const slot of deliveryTimeSlots) {
        set.add(slot.date);
      }
    }
    return set;
  }, [deliveryTimeSlots]);

  const [
    updateClientOrderTimeSlotMutation,
    { loading: clientOrderTimeSlotMutationLoading },
  ] = useMutation(UpdateClientOrderTimeSlotMutation);

  const [
    noSuitableTimeSlotMutation,
    { loading: noSuitableTimeSlotMutationLoading },
  ] = useMutation(NoSuitableTimeSlotMutation, {
    onError: () => toast.error("Une erreur est survenue"),
    onCompleted: (data) => {
      if (data?.updateClientOrderTimeSlot?.success) {
        toast.error(data.updateClientOrderTimeSlot.errorMessage);
      } else {
        toast.success("Votre choix à bien été enregistré");
      }
    },
  });

  const availableTimeSlotsForDate = React.useMemo(() => {
    if (!selectedDeliveryDate || !deliveryTimeSlots) {
      return [];
    }

    const availableDeliveryTimeSlotsForDate = deliveryTimeSlots
      .filter(
        (timeSlot: DeliveryTimeSlot) =>
          timeSlot.date === selectedDeliveryDate.format("YYYY-MM-DD"),
      )
      .map((timeSlot: DeliveryTimeSlot) => ({
        ...timeSlot,
        key: getTimeSlotKey(timeSlot) ?? "",
      }));

    return availableDeliveryTimeSlotsForDate || [];
  }, [selectedDeliveryDate, deliveryTimeSlots]);

  const handleSubmit = React.useCallback(async (): Promise<void> => {
    if (noSuitableTimeSlot) {
      setOpenDialog(true);
      return;
    }

    const timeSlot = availableTimeSlotsForDate.find(
      (availableTimeSlot) => availableTimeSlot.key === selectedTimeSlot,
    );
    if (!timeSlot) return;

    try {
      const { data } = await updateClientOrderTimeSlotMutation({
        variables: {
          clientOrderTimeSlot: {
            date: timeSlot.date,
            halfday: timeSlot.halfday,
            hour_from: timeSlot.hour_from,
            hour_to: timeSlot.hour_to,
            office_id: timeSlot.office_id,
          },
        },
      });

      if (data?.updateClientOrderTimeSlot?.errorMessage) {
        toast.error(data.updateClientOrderTimeSlot.errorMessage);
        return;
      }

      toast.success("Votre créneau a bien été enregistré");
      onSubmitSuccess?.();
    } catch (_error) {
      toast.error("Une erreur est survenue");
    }
  }, [
    availableTimeSlotsForDate,
    selectedTimeSlot,
    noSuitableTimeSlot,
    onSubmitSuccess,
    updateClientOrderTimeSlotMutation,
  ]);

  const handleSelectTimeSlot = React.useCallback((timeSlot: string): void => {
    setNoSuitableTimeSlot(false);
    setSelectedTimeSlot(timeSlot);
  }, []);

  const toggleNoSuitableTimeSlot = React.useCallback((): void => {
    setSelectedTimeSlot((prevSlot) => {
      if (prevSlot !== null) setSelectedDeliveryDate(null);
      return null;
    });
    setNoSuitableTimeSlot((prev) => !prev);
  }, []);
  const confirmedDateSameAsSelected = React.useMemo<boolean>(() => {
    if (!selectedDeliveryDate || !confirmedDate) {
      return false;
    }
    return (
      selectedDeliveryDate.isSame(confirmedDate, "day") &&
      selectedTimeSlot === getTimeSlotKey(order.timeSlot)
    );
  }, [selectedDeliveryDate, confirmedDate, selectedTimeSlot, order.timeSlot]);

  const isNewTimeslotNeeded = React.useCallback((monthDate: Dayjs) => {
    if (!fetchedUpToRef.current) return true;
    return dayjs(fetchedUpToRef.current).isBefore(monthDate.endOf("month"));
  }, []);

  const getNextMonthData = React.useCallback(
    (monthDate: Dayjs) => {
      const endDate = getEndDateForFetch(monthDate);
      // Only fetch the delta — from the day after last fetched date
      const startDate = fetchedUpToRef.current
        ? dayjs(fetchedUpToRef.current).add(1, "day").format("YYYY-MM-DD")
        : minDate.format("YYYY-MM-DD");
      fetchedUpToRef.current = endDate;
      fetchTimeSlots({
        variables: { startDate, endDate },
      });
    },
    [fetchTimeSlots, minDate, getEndDateForFetch],
  );

  const maxCalendarDate = React.useMemo<dayjs.Dayjs | undefined>(() => {
    // if the current delivery date is after the maxDate, we need to extend the calendar to the date
    if (
      maxDate &&
      selectedDeliveryDate &&
      selectedDeliveryDate.isAfter(maxDate, "day")
    ) {
      return selectedDeliveryDate;
    }
    // this is either the maxDate or undefined(infinite calendar)
    return maxDate;
  }, [maxDate, selectedDeliveryDate]);

  // Memoized CalendarPicker callbacks
  const shouldDisableDate = React.useCallback(
    (date: Dayjs) => !availableDateSet.has(date.format("YYYY-MM-DD")),
    [availableDateSet],
  );

  const renderDay = React.useCallback(
    (day: Dayjs, _selectedDays: Dayjs[], pickersDayProps: any) => {
      const dateStr = day.format("YYYY-MM-DD");
      const specials = specialSlotsMap.get(dateStr);
      const hasAfterwork = specials?.has("afterwork") ?? false;
      const hasNextDay = specials?.has("next_day") ?? false;

      const dayClass = classNames("appointment__day-wrapper", {
        "appointment__day-wrapper--afterwork":
          hasAfterwork && !pickersDayProps.disabled,
        "appointment__day-wrapper--next-day":
          hasNextDay && !hasAfterwork && !pickersDayProps.disabled,
      });

      return (
        <div key={day.toString()} className={dayClass}>
          <PickersDay {...pickersDayProps} />
        </div>
      );
    },
    [specialSlotsMap],
  );

  const handleDateChange = React.useCallback((selectedDate: Dayjs | null) => {
    setSelectedTimeSlot(null);
    setSelectedDeliveryDate(selectedDate);
    setNoSuitableTimeSlot(false);
  }, []);

  const handleMonthChange = React.useCallback(
    (monthDate: Dayjs) => {
      setSelectedTimeSlot(null);
      if (isNewTimeslotNeeded(monthDate)) {
        getNextMonthData(monthDate);
      }
    },
    [isNewTimeslotNeeded, getNextMonthData],
  );

  const handleCloseDialog = React.useCallback(() => setOpenDialog(false), []);

  // Initial fetch + re-fetch on postal code change
  React.useEffect(() => {
    setDeliveryTimeSlots([]);
    fetchedUpToRef.current = null;
    setInitialLoadComplete(false);
    const endDate = getEndDateForFetch(confirmedDate ?? minDate);
    fetchedUpToRef.current = endDate;
    fetchTimeSlots({
      variables: {
        startDate: minDate.format("YYYY-MM-DD"),
        endDate,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.client.postal_code]);

  if (!initialLoadComplete || loadingClientPortalConfig) {
    return (
      <div className="appointment__loading">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div className="appointment">
        <div className="appointment__card appointment__card--calendar">
          <div className="appointment__card-header">
            <svg
              className="appointment__header-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z" />
            </svg>
            <span>Choisissez une date</span>
          </div>
          <div className="appointment__card-body">
            <div className="appointment__calendar-container">
              <CalendarPicker
                date={selectedDeliveryDate}
                onChange={handleDateChange}
                disablePast={true}
                minDate={minDate}
                maxDate={maxCalendarDate}
                shouldDisableDate={shouldDisableDate}
                shouldDisableYear={disableAllYears}
                onMonthChange={handleMonthChange}
                views={["month", "day"]}
                renderDay={renderDay}
              />
            </div>
          </div>
        </div>
        <div className="appointment__card appointment__card--timeslots">
          <div className="appointment__card-header">
            <svg
              className="appointment__header-icon"
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="currentColor"
            >
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
            </svg>
            <span>Créneau horaire</span>
          </div>
          <div className="appointment__card-body">
            <div className="appointment__time-slot-selector-container">
              <TimeSlotSelector
                selectedTimeSlot={selectedTimeSlot}
                handleSelectTimeSlot={handleSelectTimeSlot}
                noSuitableTimeSlot={noSuitableTimeSlot}
                toggleNoSuitableTimeSlot={toggleNoSuitableTimeSlot}
                availableTimeSlotsForDate={availableTimeSlotsForDate}
                loading={
                  clientOrderTimeSlotMutationLoading ||
                  noSuitableTimeSlotMutationLoading
                }
                handleSubmit={handleSubmit}
                confirmedDateSameAsSelected={confirmedDateSameAsSelected}
                status={order?.status}
                isNoSuitableTimeslotAvailable={isNoSuitableTimeslotAvailable}
              />
            </div>
          </div>
        </div>
      </div>
      <AlertDialog
        handleClose={handleCloseDialog}
        open={openDialog}
        title="Information"
        message="Vous avez choisi d'être rappelé. Vous pouvez annuler votre choix ou cliquez sur continuer pour être contacté ultérieurement par nos services et convenir d'un créneau."
        appearence="primary"
        callback={noSuitableTimeSlotMutation}
      />
    </>
  );
};

export default Appointment;
